import { useMemo } from 'react';

import { calculateIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import type { Holding } from '@/hooks/usePortfolio';

import { useHoldingsSorting } from './useHoldingsSorting';
import type { HoldingsSorting } from './types';

type SortingFunction<T> = (a: T, b: T) => -1 | 0 | 1;

export const sortValue = <Value extends string | number>(
  a: Value | null,
  b: Value | null,
  direction: 'asc' | 'desc'
) => {
  if (a === b) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  if (direction === 'desc') {
    return a < b ? 1 : -1;
  }
  return a < b ? -1 : 1;
};

const sortingFns: Record<HoldingsSorting, SortingFunction<Holding>> = {
  'value-desc': (
    { analysis: { value: a } }: Holding,
    { analysis: { value: b } }: Holding
  ) => sortValue(a, b, 'desc'),
  'roi-desc': (
    { analysis: { roi: a } }: Holding,
    { analysis: { roi: b } }: Holding
  ) => sortValue(a, b, 'desc'),
  'company-name-a-to-z': (
    { company: { name: a } }: Holding,
    { company: { name: b } }: Holding
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  'ticker-a-to-z': (
    { company: { tickerSymbol: a } }: Holding,
    { company: { tickerSymbol: b } }: Holding
  ) => sortValue(a.toUpperCase(), b.toUpperCase(), 'asc'),
  'market-cap-desc': (
    {
      company: {
        analysisValue: { marketCapUSD: a },
      },
    }: Holding,
    {
      company: {
        analysisValue: { marketCapUSD: b },
      },
    }: Holding
  ) => sortValue(a, b, 'desc'),
  'performance-7d-desc': (
    {
      company: {
        analysisValue: { return7d: a },
      },
    }: Holding,
    {
      company: {
        analysisValue: { return7d: b },
      },
    }: Holding
  ) => sortValue(a, b, 'desc'),
  'discount-desc': (a: Holding, b: Holding) =>
    sortValue(
      calculateIntrinsicDiscount(a.company),
      calculateIntrinsicDiscount(b.company),
      'desc'
    ),
  'discount-asc': (a: Holding, b: Holding) =>
    sortValue(
      calculateIntrinsicDiscount(a.company),
      calculateIntrinsicDiscount(b.company),
      'asc'
    ),
};

export const useSortedHoldings = (holdings: Holding[] = []) => {
  const [sorting] = useHoldingsSorting();

  return useMemo(() => {
    const sortingFn = sortingFns[sorting];
    if (typeof sortingFn === 'undefined') {
      return holdings;
    }
    return [...holdings].sort(sortingFn);
  }, [holdings, sorting]);
};
